import {html, css, TemplateResult, CSSResultGroup} from 'lit'
import {customElement, property} from 'lit/decorators.js'
import {BaseComponent} from '../base-component.js'
import {arrayOrString} from '../utils/custom-types.js'

@customElement('il-card-section')
export default class CardSectionComponent extends BaseComponent {
  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-card-section {
        display: flex;
        flex-direction: column;
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-bold);
        line-height: var(--il-font-line-height-140);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-card-section__main {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        align-items: center;
      }

      .il-card-section__main .il-card-section__icon {
        flex: 0 0 auto;
      }

      .il-card-section__main .il-card-section__header {
        flex: 1 1 auto;
        display: flex;
        justify-content: start;
        gap: 8px;
      }

      .il-card-section__main
        .il-card-section__header
        > div:not(:last-child):after {
        content: '•';
        padding-left: 8px;
        color: var(--il-color-neutrals-200);
      }

      .il-card-section__main .il-card-section__action-icon {
        flex: 0 0 auto;
      }

      .il-card-section__content {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .il-card-section__content::slotted([part='spacing-top']) {
        margin-top: 12px;
      }
    `,
  ]

  @property({type: String})
  header: string = ''

  @property({type: String})
  icon: string = ''

  @property({attribute: 'action-icon', type: String})
  actionIcon: string = ''

  @property({type: Function})
  action: Function | undefined = undefined

  constructor () {
    super()

    /* START: custom properties check */
    if (this.header) {
      arrayOrString(this, 'header')
    }
    /* END: custom properties check */
  }

  iconComponent (): TemplateResult {
    if (this.icon) {
      return html` <div
        class="il-card-section__icon"
        data-test-iliad-card-section-icon
      >
        <il-image src="${this.icon}" height="20" width="20"></il-image>
      </div>`
    }

    return html``
  }

  headerComponent (): TemplateResult {
    if (this.header) {
      let headerContent: string | Array<TemplateResult> = this.header

      if (Array.isArray(this.header)) {
        headerContent = this.header.map((headerText) => {
          return html`<div>${headerText}</div>`
        })
      }

      return html` <div
        class="il-card-section__header"
        data-test-iliad-card-section-header
      >
        ${headerContent}
      </div>`
    }

    return html``
  }

  actionComponent (): TemplateResult {
    if (this.actionIcon && this.action) {
      return html` <div
        class="il-card-section__action"
        data-test-iliad-card-section-action
      >
        <il-button variant="ghost" .onClick=${this.action} icon-only>
          <il-image src="${this.actionIcon}" height="20" width="20"></il-image>
        </il-button>
      </div>`
    }

    return html``
  }

  override render (): TemplateResult {
    return html`
      <div
        class="${this.baseCSSClass} il-card-section"
        data-test-iliad-card-section
      >
        <div class="il-card-section__main">
          ${this.iconComponent()} ${this.headerComponent()}
          ${this.actionComponent()}
        </div>

        <slot class="il-card-section__content"></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-card-section': CardSectionComponent
  }
}
